import { ApiQueryParams } from '@frontend/api-utils';
import { MultiObjectSelectInput } from '@frontend/components/basic-forms';
import { Spinner } from '@frontend/elements';
import { CommonMessage, useLocale } from '@frontend/lang';
import { ProductClient } from '@frontend/product/api';
import { defaultMapWithAttributes } from '@frontend/product/badge';
import { IProduct, ProductQueryParams } from '@frontend/product/types';
import { useAccountRepository } from '@frontend/repository';
import React, { useMemo, useState } from 'react';

const ID = 'multi-kit-product-select';
export interface MultiKitProductSelectProps {
    id?: string;
    label?: React.ReactNode;
    required?: boolean;
    onChange?: (newValue: string[]) => void;
    onChangeObject?: (newValue: IProduct[]) => void;
    value?: string[];
    searchValueOverwrite?: string;
    className?: string;
    disabled?: boolean;
    isClearable?: boolean;
    allowURLChange?: boolean;
    createEnabled?: boolean;
    queryParams?: ApiQueryParams<ProductQueryParams>;
    disableFetch?: boolean;
    excludeProductId?: string | string[];
    subset?: string[];
    onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    isOpenOverwrite?: boolean;
}

export const MultiKitProductSelect = (props: MultiKitProductSelectProps) => {
    const { excludeProductId, ...selectProps } = props;
    const { locale } = useLocale();
    const { currentAccount } = useAccountRepository();
    const [v, cV] = useState<IProduct[]>([]);

    const excludedIds = useMemo(() => {
        if (!excludeProductId) return new Set<string>();
        return new Set(Array.isArray(excludeProductId) ? excludeProductId : [excludeProductId]);
    }, [excludeProductId]);

    if (!locale) return <></>;
    if (!currentAccount) return <Spinner />;

    return (
        <MultiObjectSelectInput<IProduct, ProductQueryParams>
            id={`${ID}${props.id && '-' + props.id}`}
            key={`${ID}${props.id && '-' + props.id}`}
            label={props.label || CommonMessage.OBJECTS.PRODUCT.DEFAULT}
            className={props.className}
            onChange={(v: IProduct[]) => {
                props.onChange && props.onChange(v.map((prod) => prod.id));
                props.onChangeObject && props.onChangeObject(v);
                cV(v);
            }}
            required={props.required}
            queryParams={{ ...props.queryParams }}
            value={props.value || v.map((product) => product.id) || []}
            searchValueOverwrite={props.searchValueOverwrite}
            isClearable={props.isClearable}
            fetch={(arg) =>
                ProductClient.fetchProductsNotInKit(currentAccount, { ...arg, sort_field: 'name', sort_direction: 'asc' }).then((res) => {
                    if (excludedIds.size === 0) return res;
                    return {
                        ...res,
                        results: res.results.filter((p) => !excludedIds.has(p.id))
                    };
                })
            }
            multiFetch={(ids) => ProductClient.resolveProducts(ids).then((res) => res.results)}
            itemLabel={(prod: IProduct) => defaultMapWithAttributes(prod, locale, ['ean', 'sku'])}
            disableFetch={props.disableFetch}
            disabled={props.disabled}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            subset={props.subset}
            isOpenOverwrite={props.isOpenOverwrite}
        />
    );
};
